import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { type MediaUsageGranularity } from '@okvc/schema';

import { formatBytes } from './format.js';
import PatientFilter from './PatientFilter.js';
import SummaryStat from './SummaryStat.js';
import { useMediaUsage } from '../../api/media-status.api.js';
import { type Patient } from '../../models/index.js';

const PERIOD_LABEL: Record<MediaUsageGranularity, string> = {
  hour: 'Hour (UTC)',
  day: 'Day',
  month: 'Month',
  all: 'Period',
};

const DataUsageTab = () => {
  const [granularity, setGranularity] = useState<MediaUsageGranularity>('day');
  const [month, setMonth] = useState<DateTime>(DateTime.utc().startOf('month'));
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const monthFiltered = granularity === 'hour' || granularity === 'day';
  const usage = useMediaUsage({
    granularity,
    month: monthFiltered ? month.toFormat('yyyy-MM') : undefined,
    patientId: selectedPatient?.id,
  });

  const items = useMemo(() => usage.data?.items ?? [], [usage.data]);
  const totals = usage.data?.totals ?? { bytes: 0, highBytes: 0, lowBytes: 0 };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Data received from cameras, recorded per hour. Months are UTC calendar months.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <SummaryStat label="Total" value={formatBytes(totals.bytes)} emphasised />
            <SummaryStat label="High quality" value={formatBytes(totals.highBytes)} />
            <SummaryStat label="Low quality" value={formatBytes(totals.lowBytes)} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <PatientFilter
              id="media-usage-patient-filter"
              value={selectedPatient}
              onChange={setSelectedPatient}
            />
            <DatePicker
              label="Month"
              views={['year', 'month']}
              openTo="month"
              value={month}
              onChange={(value) => value && setMonth(value.startOf('month'))}
              disabled={!monthFiltered}
              disableFuture
              slotProps={{ textField: { size: 'small' } }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, value: MediaUsageGranularity | null) => value && setGranularity(value)}
            >
              <ToggleButton value="hour">Hourly</ToggleButton>
              <ToggleButton value="day">Daily</ToggleButton>
              <ToggleButton value="month">Monthly</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{PERIOD_LABEL[granularity]}</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Quality</TableCell>
              <TableCell align="right">Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`${item.period}::${item.patientId}::${item.quality}`} hover>
                <TableCell>{item.period}</TableCell>
                <TableCell>{item.patientName ?? item.patientId ?? '—'}</TableCell>
                <TableCell>{item.quality}</TableCell>
                <TableCell align="right">{formatBytes(item.bytes)}</TableCell>
              </TableRow>
            ))}
            {!usage.isLoading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  No data usage recorded in this period.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DataUsageTab;
